import React, { useMemo, useState } from "react";
import {
  ArrowUpRight,
  Loader2,
  LogOut,
  Mail,
  Phone,
  Building2,
  Download,
  RefreshCw,
  Search,
  ShieldCheck,
  Inbox,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAdminAuth, useQuotes } from "../hooks/useAdmin";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).replace(/"/g, '""');
  return `"${s}"`;
};

const downloadCsv = (rows) => {
  const headers = [
    "created_at",
    "name",
    "company",
    "email",
    "phone",
    "product_interest",
    "monthly_quantity",
    "private_label",
    "message",
    "source_page",
    "id",
  ];
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => csvEscape(r[h])).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `blacpac-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

function LoginCard({ onSubmit }) {
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setSubmitting(true);
    onSubmit(value.trim());
    setSubmitting(false);
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6">
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={handleSubmit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-3xl border border-[#E85D2F]/20 bg-[#121211] p-8 md:p-10"
      >
        <div className="flex items-center gap-3 mb-7">
          <span className="h-12 w-12 rounded-2xl bg-[#E85D2F] flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-[#0A0A09]" strokeWidth={2.2} />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#E85D2F] font-semibold">
              BlacPac admin
            </p>
            <h1 className="font-grotesk text-2xl font-black tracking-tight text-[#F7F5F0]">
              Enquiries inbox
            </h1>
          </div>
        </div>

        <p className="text-sm text-[#F7F5F0]/65 leading-relaxed mb-7">
          Paste your admin pass-phrase to view every quote request submitted on
          the site. You only need to do this once per device — we&rsquo;ll
          remember you on this browser.
        </p>

        <div className="space-y-2">
          <Label htmlFor="admin-token" className="text-xs uppercase tracking-[0.18em] text-[#E85D2F] font-semibold">
            Pass-phrase
          </Label>
          <Input
            id="admin-token"
            data-testid="admin-token-input"
            type="password"
            autoComplete="current-password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter your admin pass-phrase"
            className="bg-transparent border-0 border-b border-[#E85D2F]/30 rounded-none px-0 text-[#F7F5F0] placeholder:text-[#F7F5F0]/30 focus-visible:ring-0 focus-visible:border-[#E85D2F] py-3 h-auto"
          />
        </div>

        <button
          type="submit"
          disabled={submitting || !value.trim()}
          data-testid="admin-login-submit"
          className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#E85D2F] text-[#0A0A09] px-6 py-4 font-semibold hover:bg-[#F47A4A] transition-colors disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Open inbox <ArrowUpRight className="h-4 w-4" /></>}
        </button>
      </motion.form>
    </div>
  );
}

function QuoteRow({ q }) {
  const [open, setOpen] = useState(false);
  const subject = encodeURIComponent(`Re: BlacPac enquiry — ${q.product_interest || "your packaging"}`);
  const body = encodeURIComponent(
    `Hi ${q.name?.split(" ")[0] || "there"},\n\nThanks for reaching out to BlacPac. ` +
    `I'm getting back to you about your enquiry for ${q.product_interest || "packaging"}.\n\n— BlacPac team`
  );

  return (
    <motion.div
      layout
      data-testid={`quote-row-${q.id}`}
      className="rounded-2xl border border-[#E85D2F]/15 bg-[#121211] overflow-hidden"
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 hover:bg-[#1A1A18] transition-colors"
      >
        <div className="flex-1 min-w-0">
          <p className="font-grotesk font-bold text-[#F7F5F0] tracking-tight truncate">
            {q.name || "—"}
            {q.company && (
              <span className="ml-2 text-[#F7F5F0]/50 font-normal">· {q.company}</span>
            )}
          </p>
          <p className="text-xs text-[#F7F5F0]/55 mt-1 truncate">
            {q.product_interest || "—"}
            {q.monthly_quantity && <> · {q.monthly_quantity}/mo</>}
            {q.private_label && <span className="ml-2 text-[#E85D2F]">· Private label</span>}
          </p>
        </div>
        <div className="text-xs text-[#F7F5F0]/50 sm:text-right shrink-0">
          {formatDate(q.created_at)}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="border-t border-[#F7F5F0]/5 overflow-hidden"
          >
            <div className="px-5 py-5 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <a
                  href={`mailto:${q.email}?subject=${subject}&body=${body}`}
                  data-testid={`quote-reply-${q.id}`}
                  className="flex items-center gap-3 rounded-xl bg-[#0A0A09] border border-[#E85D2F]/20 px-4 py-3 hover:border-[#E85D2F]/60 transition-colors group"
                >
                  <Mail className="h-4 w-4 text-[#E85D2F]" />
                  <span className="text-[#F7F5F0] truncate">{q.email}</span>
                  <ArrowUpRight className="h-3.5 w-3.5 ml-auto text-[#F7F5F0]/50 group-hover:text-[#E85D2F] transition-colors" />
                </a>
                {q.phone && (
                  <a
                    href={`tel:${q.phone.replace(/\s+/g, "")}`}
                    className="flex items-center gap-3 rounded-xl bg-[#0A0A09] border border-[#E85D2F]/20 px-4 py-3 hover:border-[#E85D2F]/60 transition-colors"
                  >
                    <Phone className="h-4 w-4 text-[#E85D2F]" />
                    <span className="text-[#F7F5F0]">{q.phone}</span>
                  </a>
                )}
                {q.company && (
                  <div className="flex items-center gap-3 rounded-xl bg-[#0A0A09] border border-[#F7F5F0]/10 px-4 py-3 sm:col-span-2">
                    <Building2 className="h-4 w-4 text-[#F7F5F0]/50" />
                    <span className="text-[#F7F5F0]">{q.company}</span>
                  </div>
                )}
              </div>

              {q.message && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E85D2F] font-semibold mb-2">
                    Message
                  </p>
                  <p className="text-sm text-[#F7F5F0]/85 leading-relaxed whitespace-pre-wrap">
                    {q.message}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <Stat label="Product" value={q.product_interest || "—"} />
                <Stat label="Monthly qty" value={q.monthly_quantity || "—"} />
                <Stat label="Private label" value={q.private_label ? "Yes" : "No"} />
                <Stat label="Source" value={q.source_page || "—"} />
              </div>

              <p className="text-[10px] text-[#F7F5F0]/30 tracking-wider">ID · {q.id}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-[#0A0A09] border border-[#F7F5F0]/5 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-[#F7F5F0]/50 font-semibold">
        {label}
      </p>
      <p className="text-sm text-[#F7F5F0] mt-1 truncate">{value}</p>
    </div>
  );
}

export default function AdminPage() {
  const { token, setToken, client } = useAdminAuth();
  const { data, loading, error, refetch } = useQuotes(client);
  const [query, setQuery] = useState("");

  const handleLogin = async (value) => {
    setToken(value);
    toast.success("Welcome back. Loading your inbox…");
  };

  const handleLogout = () => {
    setToken("");
    toast("Signed out.");
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return data;
    const q = query.toLowerCase();
    return data.filter((row) =>
      [row.name, row.email, row.company, row.product_interest, row.phone, row.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [data, query]);

  if (!token) {
    return <LoginCard onSubmit={handleLogin} />;
  }

  if (error === "unauthorised") {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6">
        <p className="text-[#E85D2F] font-grotesk uppercase tracking-[0.25em] text-xs">Access denied</p>
        <h2 className="mt-4 font-grotesk text-3xl md:text-4xl font-black tracking-tighter text-[#F7F5F0]">
          That pass-phrase didn&rsquo;t work.
        </h2>
        <button
          onClick={handleLogout}
          data-testid="admin-retry-login"
          className="mt-6 inline-flex items-center gap-3 rounded-full bg-[#E85D2F] text-[#0A0A09] px-6 py-3 font-semibold hover:bg-[#F47A4A] transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="bg-[#0A0A09] min-h-[80vh]">
      <section className="border-b border-[#F7F5F0]/5 pb-6 pt-2">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#E85D2F] font-semibold">
                BlacPac admin
              </p>
              <h1 className="mt-3 font-grotesk text-4xl md:text-5xl font-black tracking-tighter text-[#F7F5F0]">
                Enquiries inbox
                <span className="text-[#E85D2F]">.</span>
              </h1>
              <p className="mt-3 text-sm text-[#F7F5F0]/60 max-w-md">
                Every quote request submitted on the site lands here. Tap a row
                to see the full message and reply by email.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={refetch}
                disabled={loading}
                data-testid="admin-refresh"
                className="inline-flex items-center gap-2 rounded-full border border-[#F7F5F0]/15 text-[#F7F5F0] px-4 py-2 text-sm hover:border-[#E85D2F]/50 hover:text-[#E85D2F] transition-colors disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <button
                onClick={() => downloadCsv(filtered)}
                disabled={!filtered.length}
                data-testid="admin-export-csv"
                className="inline-flex items-center gap-2 rounded-full bg-[#E85D2F] text-[#0A0A09] px-4 py-2 text-sm font-semibold hover:bg-[#F47A4A] transition-colors disabled:opacity-60"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </button>
              <button
                onClick={handleLogout}
                data-testid="admin-logout"
                className="inline-flex items-center gap-2 rounded-full border border-[#F7F5F0]/15 text-[#F7F5F0]/70 px-4 py-2 text-sm hover:text-[#F7F5F0] hover:border-[#F7F5F0]/30 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-12">
          {/* Stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <SummaryCard label="Total enquiries" value={data.length} icon={Inbox} />
            <SummaryCard label="Filtered" value={filtered.length} icon={Search} />
            <SummaryCard
              label="Private label"
              value={data.filter((r) => r.private_label).length}
              icon={Sparkles}
            />
            <SummaryCard
              label="With phone"
              value={data.filter((r) => r.phone).length}
              icon={Phone}
            />
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#F7F5F0]/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="admin-search"
              placeholder="Search name, email, company, product…"
              className="w-full bg-[#121211] border border-[#F7F5F0]/10 rounded-full pl-11 pr-5 py-3 text-sm text-[#F7F5F0] placeholder:text-[#F7F5F0]/35 focus:outline-none focus:border-[#E85D2F]/50 transition-colors"
            />
          </div>

          {/* List */}
          {loading && (
            <div className="flex items-center justify-center py-16 text-[#F7F5F0]/60">
              <Loader2 className="h-6 w-6 animate-spin mr-3 text-[#E85D2F]" />
              Loading enquiries…
            </div>
          )}

          {!loading && error === "error" && (
            <div className="rounded-2xl border border-[#E85D2F]/30 bg-[#121211] p-6 text-center">
              <p className="text-[#F47A4A]">
                Couldn&rsquo;t reach the server. Check your connection and try again.
              </p>
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="rounded-2xl border border-[#F7F5F0]/10 bg-[#121211] p-10 text-center">
              <Inbox className="h-8 w-8 text-[#E85D2F] mx-auto mb-4" />
              <p className="font-grotesk text-xl font-bold text-[#F7F5F0]">
                {data.length === 0 ? "No enquiries yet." : "No matches for your search."}
              </p>
              <p className="text-sm text-[#F7F5F0]/55 mt-2">
                {data.length === 0
                  ? "When someone submits a quote request on the site, it'll appear here."
                  : "Try a different keyword or clear the search."}
              </p>
            </div>
          )}

          <div className="space-y-3">
            {filtered.map((q) => (
              <QuoteRow key={q.id} q={q} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-[#E85D2F]/15 bg-[#121211] p-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#F7F5F0]/50 font-semibold">
          {label}
        </p>
        <Icon className="h-4 w-4 text-[#E85D2F]" />
      </div>
      <p className="mt-3 font-grotesk text-3xl font-black tracking-tighter text-[#F7F5F0]">
        {value}
      </p>
    </div>
  );
}
